//
//  Births.tsx
//  TrajeXY Signet Server
//
//  Copyright © 2026 TrajeXY. All rights reserved.
//
//  Created: September 23, 2026
//  Author: Bob Keathley
//
//  Births page with two sections: canon birth records (spec 6a) and birth
//  certificates (6d, SIG-38). Real empty state; no invented rows.
//
import { useCallback, useEffect, useState } from 'react'
import { api } from '@shared/api/client'
import { isOk } from '@shared/api/envelope'
import { templateRef, type BirthCertSummary, type CanonBirth, type Page } from '@shared/api/types'
import type { Session } from '@shared/auth'
import { href } from '@shared/router'

const when = (s: number) => new Date(s * 1000).toLocaleString()

/**
 * `/births` -- two distinct things, clearly separated:
 * 1. Canon birth records (`GET /v1/canon/births`, §6a) -- real, storable today; shown as a list
 *    with a detail view.
 * 2. Birth certificates (§6d, SIG-38 Step 4) -- real rows from `GET /v1/notebooks/birth-certs`,
 *    with a detail view. No invented rows; a real empty state when there are none.
 */
export default function Births({ session, openId }: { session: Session; openId?: string | null }) {
  return (
    <>
      <CanonBirths session={session} openId={openId} />
      <BirthCertificates session={session} />
    </>
  )
}

function CanonBirths({ session, openId }: { session: Session; openId?: string | null }) {
  const [items, setItems] = useState<CanonBirth[] | 'unavailable' | null>(null)
  const [next, setNext] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [detailId, setDetailId] = useState<string | null>(null)
  const [detail, setDetail] = useState<CanonBirth | 'not_found' | 'unavailable' | null>(null)

  // Cursor/limit are reserved but not yet paginated server-side (§6a: `next_cursor` is always
  // `null` today) -- "Load older" below is a no-op until the server starts returning one.
  const load = useCallback(async (cursor: number | null) => {
    const q = cursor === null ? '' : `?cursor=${cursor}`
    const r = await api<Page<CanonBirth>>(`/canon/births${q}`, {}, session.token)
    if (isOk(r)) { setItems((prev) => (cursor === null ? r.data.items : [...(Array.isArray(prev) ? prev : []), ...r.data.items])); setNext(r.data.next_cursor) }
    else { setItems('unavailable'); setError(r.error.message) }
  }, [session.token])
  useEffect(() => { setItems(null); load(null) }, [load])

  const openDetail = useCallback(async (id: string) => {
    setDetailId(id); setDetail(null)
    const r = await api<CanonBirth>(`/canon/births/${id}`, {}, session.token)
    if (isOk(r)) { setDetail(r.data); return }
    setDetail(r.error.code === 'not_found' ? 'not_found' : 'unavailable')
  }, [session.token])

  // A "birth already recorded as <rn_id>" link from #/birth (409 canon_birth_exists) lands here
  // as `?id=<existing_id>` -- open that record's detail automatically.
  useEffect(() => { if (openId) openDetail(openId) }, [openId, openDetail])

  return (
    <>
      <h2>Canon birth records</h2>
      <p className="hint">Every stored canon identity for this tenant, newest first (§6a). A birth
        record is immutable once stored -- it is never edited or deleted here.</p>
      {error && <p className="err" role="alert">{error}</p>}
      <CanonBirthTable items={items} onDetail={openDetail} />
      {next !== null && Array.isArray(items) && <p><button className="btn ghost" onClick={() => load(next)}>Load older</button></p>}

      {detailId && (
        <div className="card" role="status" style={{ marginTop: 12 }}>
          <div className="l">Record {detailId.slice(0, 8)}…</div>
          {detail === null && <p className="hint">Loading…</p>}
          {detail === 'not_found' && <p className="err" role="alert">404 not found -- this record doesn't exist, or belongs to a different tenant.</p>}
          {detail === 'unavailable' && <p className="err" role="alert">Couldn't load this record. Try again.</p>}
          {detail && detail !== 'not_found' && detail !== 'unavailable' && (
            <>
              <p className="mono">{detail.record.rn_id}</p>
              <p>Status: {detail.record.status === 'QUARANTINE-REVIEW' ? <span className="pill off">QUARANTINE-REVIEW</span> : <span className="pill ok">identity-bearing</span>}</p>
              <p className="hint">Genesis seal: <span className="mono">{detail.record.genesis_seal}</span></p>
              <p className="hint">Tripwire margin: <span className="mono">{detail.record.genesis.adversarial_tripwire.margin.toFixed(3)}</span></p>
              <p className="hint">{detail.labels.k} · {detail.labels.tripwire} · {detail.labels.risk_slope} · {detail.labels.base} · {detail.labels.corpus}</p>
            </>
          )}
          <p><button className="btn ghost sm" onClick={() => { setDetailId(null); setDetail(null) }}>close</button></p>
        </div>
      )}
    </>
  )
}

/** The list body, split out so it can be rendered directly from a fixed `items` array in tests --
 * `CanonBirths` above is the stateful fetcher; this is the pure presentation. */
export function CanonBirthTable({ items, onDetail }: { items: CanonBirth[] | 'unavailable' | null; onDetail: (id: string) => void }) {
  return (
    <table>
      <thead><tr><th>Record</th><th>Agent</th><th>Sector</th><th>Status</th><th>Created by</th><th>Created</th><th></th></tr></thead>
      <tbody>
        {items === null && <tr><td colSpan={7} className="hint">Loading…</td></tr>}
        {items === 'unavailable' && <tr><td colSpan={7} className="hint">Canon birth records aren't reachable right now.</td></tr>}
        {Array.isArray(items) && items.length === 0 && <tr><td colSpan={7} className="hint">No canon births yet. <a href={href('/birth')}>Run the canon interview ›</a></td></tr>}
        {Array.isArray(items) && items.map((b) => (
          <tr key={b.id}>
            <td className="mono">{b.record.rn_id}</td>
            <td className="mono">{b.agent_name}</td>
            <td>{b.sector}</td>
            <td>{b.record.status === 'QUARANTINE-REVIEW' ? <span className="pill off">QUARANTINE-REVIEW</span> : <span className="pill ok">identity-bearing</span>}</td>
            <td className="hint mono">{b.created_by.slice(0, 8)}…</td>
            <td className="hint">{when(b.created_at)}</td>
            <td style={{ textAlign: 'right' }}><button className="btn ghost sm" onClick={() => onDetail(b.id)}>detail</button></td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

/** §6d `GET /v1/notebooks/birth-certs` / `/{id}` -- the fetcher for `#/births`'s certificates
 * section. `BirthCertTable` below is the pure presentation, split out so it can render from a
 * fixed `items` array in tests. */
export function BirthCertificates({ session }: { session: Session }) {
  const [items, setItems] = useState<BirthCertSummary[] | 'unavailable' | null>(null)
  const [next, setNext] = useState<number | null>(null)
  const [detailId, setDetailId] = useState<string | null>(null)
  const [detail, setDetail] = useState<BirthCertSummary | 'not_found' | 'unavailable' | null>(null)

  const load = useCallback(async (cursor: number | null) => {
    const q = cursor === null ? '' : `?cursor=${cursor}`
    const r = await api<Page<BirthCertSummary>>(`/notebooks/birth-certs${q}`, {}, session.token)
    if (isOk(r)) { setItems((prev) => (cursor === null ? r.data.items : [...(Array.isArray(prev) ? prev : []), ...r.data.items])); setNext(r.data.next_cursor) }
    else setItems('unavailable')
  }, [session.token])
  useEffect(() => { setItems(null); load(null) }, [load])

  const openDetail = useCallback(async (id: string) => {
    setDetailId(id); setDetail(null)
    const r = await api<BirthCertSummary>(`/notebooks/birth-certs/${id}`, {}, session.token)
    if (isOk(r)) { setDetail(r.data); return }
    setDetail(r.error.code === 'not_found' ? 'not_found' : 'unavailable')
  }, [session.token])

  return (
    <>
      <h2>Birth certificates</h2>
      <p className="hint">Every Notebook birth for this tenant, newest first (§6d). A <span className="pill ok">born</span> Notebook
        was signed the moment it was born in the console. A <span className="pill">pending</span> code for an older Mac
        carries no signature yet -- the template version is resolved and signed only when that Mac redeems it.</p>
      <BirthCertTable items={items} onDetail={openDetail} />
      {next !== null && Array.isArray(items) && <p><button className="btn ghost" onClick={() => load(next)}>Load older</button></p>}

      {detailId && (
        <div className="card" role="status" style={{ marginTop: 12 }}>
          <div className="l">Certificate {detailId.slice(0, 8)}…</div>
          {detail === null && <p className="hint">Loading…</p>}
          {detail === 'not_found' && <p className="err" role="alert">404 not found -- this request doesn't exist, or belongs to a different tenant.</p>}
          {detail === 'unavailable' && <p className="err" role="alert">Couldn't load this record. Try again.</p>}
          {detail && detail !== 'not_found' && detail !== 'unavailable' && <BirthCertDetail cert={detail} />}
          <p><button className="btn ghost sm" onClick={() => { setDetailId(null); setDetail(null) }}>close</button></p>
        </div>
      )}
    </>
  )
}

const statusPill = (s: BirthCertSummary['status']) => {
  if (s === 'born') return <span className="pill ok">born · signed</span>
  if (s === 'redeemed') return <span className="pill ok">redeemed</span>
  if (s === 'refused' || s === 'expired') return <span className="pill off">{s}</span>
  return <span className="pill">{s}</span>
}

export function BirthCertTable({ items, onDetail }: { items: BirthCertSummary[] | 'unavailable' | null; onDetail: (id: string) => void }) {
  return (
    <table>
      <thead><tr><th>Certificate</th><th>Notebook ID</th><th>Agent</th><th>Status</th><th>Template version</th><th>Issuer</th><th>Signed / redeemed by, when</th><th></th></tr></thead>
      <tbody>
        {items === null && <tr><td colSpan={8} className="hint">Loading…</td></tr>}
        {items === 'unavailable' && <tr><td colSpan={8} className="hint">Birth certificates aren't reachable right now.</td></tr>}
        {Array.isArray(items) && items.length === 0 && <tr><td colSpan={8} className="hint">No birth certificates yet. <a href={href('/birth')}>Start a birth ›</a></td></tr>}
        {Array.isArray(items) && items.map((c) => (
          <tr key={c.id}>
            <td className="mono">{c.id.slice(0, 8)}…</td>
            <td className="hint mono">{c.published_notebook_id ?? "—"}</td>
            <td className="mono">{c.agent}{c.source === 'template' && <div className="hint" style={{ fontFamily: 'inherit' }}>{c.title || '(untitled)'} · <span className="mono">{templateRef(c.template)}</span></div>}</td>
            <td>
              {statusPill(c.status)}
              {c.canon_status === 'QUARANTINE-REVIEW' && <span className="badge b-watch" style={{ marginLeft: 6 }}>QUARANTINE-REVIEW</span>}
              {c.refused_reason && <span className="hint mono" style={{ marginLeft: 6 }}>({c.refused_reason})</span>}
            </td>
            <td className="hint mono">{c.cert?.template_version ?? '—'}</td>
            <td className="hint mono">{c.issued_by.slice(0, 8)}… ({c.issuer_authority})</td>
            <td className="hint mono">{c.cert?.redeemed_by_machine ? `${c.cert.redeemed_by_machine.slice(0, 8)}… @ ${when(c.cert.redeemed_at!)}` : c.status === 'born' && c.cert?.born_in_console_at ? `signed in the console @ ${when(c.cert.born_in_console_at)}` : '—'}</td>
            <td style={{ textAlign: 'right' }}><button className="btn ghost sm" onClick={() => onDetail(c.id)}>detail</button></td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

/** The detail view: full request + cert state, including the refusal reason when one exists. No
 * private key material or raw signed bytes are ever part of this shape (§6d -- those live only in
 * the one-shot redeem response and the DB). */
export function BirthCertDetail({ cert }: { cert: BirthCertSummary }) {
  return (
    <>
      <p className="mono">{cert.agent} ({cert.kind}{cert.team ? `, ${cert.team}` : ''})</p>
      <p className="hint">Notebook ID: <span className="mono">{cert.published_notebook_id ?? "not issued yet"}</span></p>
      <p>Status: {statusPill(cert.status)} {cert.canon_status && (
        cert.canon_status === 'QUARANTINE-REVIEW'
          ? <span className="badge b-watch" style={{ marginLeft: 6 }}>QUARANTINE-REVIEW</span>
          : <span className="pill ok" style={{ marginLeft: 6 }}>identity-bearing</span>
      )}</p>
      {cert.source === 'template' && <p>Title: {cert.title || <span className="hint">(untitled)</span>}</p>}
      {cert.template && <p className="hint">Template: <span className="mono">{templateRef(cert.template)}</span></p>}
      {cert.note && <p className="hint">Note: {cert.note}</p>}
      <p className="hint">Issued by <span className="mono">{cert.issued_by}</span> ({cert.issuer_authority}) at {when(cert.issued_at)}</p>
      {cert.refused_reason && (
        // Present for `refused`, and for a touched, terminally-refused `expired` request (§6d,
        // read from the latest `birth.redeem_refused` audit row -- an untouched expired request
        // simply has no reason).
        <p className="err" role="alert">Refused -- {cert.refused_reason}.</p>
      )}
      {cert.cert && (
        <>
          <p className="hint">Resolved template version: <span className="mono">{cert.cert.template_version ?? '—'}</span>{cert.cert.resolved_at && <> at {when(cert.cert.resolved_at)}</>}</p>
          <p className="hint">Key: <span className="mono">{cert.cert.key_id ?? '—'}</span></p>
          {cert.cert.born_in_console_at && <p className="hint">Born and signed in the console at {when(cert.cert.born_in_console_at)}. It reaches Macs in the next policy release.</p>}
          {cert.cert.redeemed_by_machine && <p className="hint">Redeemed by <span className="mono">{cert.cert.redeemed_by_machine}</span> at {when(cert.cert.redeemed_at!)}</p>}
          {cert.cert.attested_notebook_id
            ? <p className="hint">Attested notebook: <span className="mono">{cert.cert.attested_notebook_id}</span> at {when(cert.cert.attested_at!)}</p>
            : <p className="hint">Not attested yet.</p>}
        </>
      )}
    </>
  )
}
